/**
 * Phone picking (Suri, 2026-10-09). Pickers are paid per pick at month end: ONE completed order = ONE pick,
 * however many SKUs it has. The record is made on the server when the last line of an order is confirmed.
 *
 * List screen: batches created in Mintsoft (yours first). Work screen: one line at a time in walking order -
 * go to the location, take the item, scan it (barcode or SKU). Wrong item = red + not counted. Items with no
 * barcode: "No barcode - confirm manually" (recorded and reported to the office). "Undo last" takes back your
 * last action. "Can't find it" skips the order to the end of the walk (or marks it short). Batches with the label
 * check on end each order with a scan of its pre-printed label.
 */
import { BarcodeFormat, DecodeHintType } from '@zxing/library';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { api, ApiError, ApiNetworkError } from '../api.js';
import { escapeHtml, qs } from '../dom.js';
import { signal } from '../feedback.js';
import type { BatchPickView, PickBatchSummary, PickConfirmResult, PickStep, PickStock } from '../types.js';

// Stock beside "Take N" (on hand / available / in this location). Looked up while the picker walks to the shelf; kept for 45 seconds.
const STOCK_TTL_MS = 45_000;
type StockEntry = { at: number; stock: PickStock | null; error: string };
const stockCache = new Map<string, StockEntry>();
const stockInFlight = new Set<string>();
const stockRetried = new Set<string>();
const stockKey = (st: { sku: string; location: string }): string => `${st.sku}\u0000${st.location}`;

function stockHtml(st: PickStep, key: string): string {
  const hit = stockCache.get(key);
  if (!hit) return '<span class="pick-stock-wait">Checking stock…</span>';
  const s = hit.stock;
  const tile = (n: number | string, label: string, bad = false): string => `<div class="${bad ? 'bad' : ''}"><b>${n}</b><span>${label}</span></div>`;
  if (!s) {
    // Live figure not available: show the on-hand the pick list knew, no explanation text.
    const onHand = st.listOnHand === undefined ? '-' : String(st.listOnHand);
    return `<div class="pick-stock-tiles">${tile(st.qty, 'Take')}${tile(onHand, st.listOnHand === undefined ? 'On hand' : 'On hand (list)')}</div>`;
  }
  const low = s.onHand < st.qty;
  const hereLow = s.here !== null && s.here.onHand < st.qty;
  const tiles = `<div class="pick-stock-tiles">${tile(st.qty, 'Take')}${tile(s.onHand, 'On hand', low)}</div>`;
  const here =
    s.here === null
      ? ''
      : `<p class="pick-stock-here ${hereLow ? 'bad' : ''}">${
          s.here.onHand === 0 ? 'Mintsoft shows none of this at this location' : `At this location: <b>${s.here.onHand}</b>`
        }</p>`;
  return tiles + here;
}

const LABEL_CAMERA_HINTS = new Map<DecodeHintType, unknown>([
  [DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.CODE_128, BarcodeFormat.CODE_39, BarcodeFormat.ITF, BarcodeFormat.DATA_MATRIX, BarcodeFormat.QR_CODE, BarcodeFormat.PDF_417, BarcodeFormat.EAN_13, BarcodeFormat.CODABAR]],
]);

const CAMERA_HINTS = new Map<DecodeHintType, unknown>([
  [DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.EAN_13, BarcodeFormat.EAN_8, BarcodeFormat.UPC_A, BarcodeFormat.UPC_E, BarcodeFormat.CODE_128]],
]);

function describeError(err: unknown): string {
  if (err instanceof ApiNetworkError) return 'No connection - check the wifi and scan again.';
  if (err instanceof ApiError) return err.message;
  return String(err);
}

function askOtherBatch(owner: string, go: () => void): void {
  const backdrop = document.createElement('div');
  backdrop.className = 'modal-backdrop';
  backdrop.innerHTML = `<div class="modal"><h2>Pick ${escapeHtml(owner)}'s batch?</h2><p>This batch is for <b>${escapeHtml(owner)}</b>. If you pick it, every order you finish is recorded under <b>your</b> name.</p><div class="actions-row" style="gap:0.5rem;"><button class="ghost" id="no">Cancel</button><button class="secondary" id="yes">Pick it anyway</button></div></div>`;
  document.body.appendChild(backdrop);
  qs<HTMLButtonElement>(backdrop, '#no').addEventListener('click', () => backdrop.remove());
  qs<HTMLButtonElement>(backdrop, '#yes').addEventListener('click', () => {
    backdrop.remove();
    go();
  });
}

export async function renderPickList(container: HTMLElement): Promise<void> {
  const div = document.createElement('div');
  div.className = 'screen';
  div.innerHTML = `
    <div class="topbar"><h1>Picking</h1><div class="actions-row"><button class="ghost" id="home">Home</button><button class="ghost" id="refresh">Refresh</button></div></div>
    <div id="body"><p class="hint center">Loading…</p></div>
  `;
  container.appendChild(div);
  qs<HTMLButtonElement>(div, '#home').addEventListener('click', () => {
    location.hash = '#/';
  });
  qs<HTMLButtonElement>(div, '#refresh').addEventListener('click', () => void load());
  const body = qs<HTMLDivElement>(div, '#body');

  function card(b: PickBatchSummary): string {
    const pct = b.orders > 0 ? Math.round((b.picked / b.orders) * 100) : 0;
    const done = b.picked + b.short >= b.orders;
    return `<button type="button" class="pick-batch ${done ? 'done' : ''}" data-run="${escapeHtml(b.runId)}" data-batch="${escapeHtml(b.batchId)}">
      <div class="pick-batch-name">${escapeHtml(b.name)}</div>
      <div class="pick-batch-meta">${escapeHtml(b.runDate)} · ${escapeHtml(b.label)} · ${escapeHtml(b.assignedTo || b.picker || 'no picker')}${b.labelCheck ? ' · label scan' : ''}</div>
      <div class="pick-bar"><i style="width:${pct}%"></i></div>
      <div class="pick-batch-meta"><b>${b.picked}</b> of ${b.orders} orders${done ? ' · finished' : ''}${b.short > 0 ? ` · <span class="error-text">${b.short} short</span>` : ''}</div>
    </button>`;
  }

  async function load(): Promise<void> {
    body.innerHTML = '<p class="hint center">Loading…</p>';
    try {
      const { batches } = await api.pickBatches();
      if (batches.length === 0) {
        body.innerHTML = '<div class="card"><p>No batches to pick yet.</p><p class="hint">The office creates them in Mintsoft from the pick list page. Pull down or tap Refresh once they are ready.</p></div>';
        return;
      }
      const open = (list: PickBatchSummary[]) => list.filter((b) => b.picked + b.short < b.orders);
      const finished = batches.filter((b) => b.picked + b.short >= b.orders);
      const mine = open(batches.filter((b) => b.mine));
      const others = open(batches.filter((b) => !b.mine));
      body.innerHTML =
        `${mine.length ? `<h2>Your batches</h2>${mine.map(card).join('')}` : ''}` +
        `${others.length ? `<h2>${mine.length ? 'Other batches' : 'Batches'}</h2>${others.map(card).join('')}` : ''}` +
        `${finished.length ? `<h2>Finished</h2>${finished.map(card).join('')}` : ''}`;
      body.querySelectorAll<HTMLButtonElement>('.pick-batch').forEach((el) =>
        el.addEventListener('click', () => {
          const b = batches.find((x) => x.batchId === el.dataset.batch);
          const go = (): void => {
            location.hash = `#/pick/${encodeURIComponent(el.dataset.run ?? '')}/${encodeURIComponent(el.dataset.batch ?? '')}`;
          };
          // Someone else's batch: ask first (helping out is fine, but the pick is recorded under YOUR name).
          if (b && !b.mine && b.assignedTo !== '' && b.picked + b.short < b.orders) askOtherBatch(b.assignedTo, go);
          else go();
        }),
      );
    } catch (err) {
      body.innerHTML = `<div class="card"><p class="error-text">${escapeHtml(describeError(err))}</p></div>`;
    }
  }
  await load();
}

export async function renderPickWork(container: HTMLElement, runId: string, batchId: string): Promise<void> {
  const div = document.createElement('div');
  div.className = 'screen';
  div.innerHTML = `<div class="topbar"><h1>Picking</h1></div><p class="hint center">Loading…</p>`;
  container.appendChild(div);

  let view: BatchPickView;
  try {
    view = await api.pickBatch(runId, batchId);
  } catch (err) {
    div.innerHTML = `<div class="topbar"><h1>Picking</h1></div><div class="card"><p class="error-text">${escapeHtml(describeError(err))}</p></div><div class="footer-actions"><button class="ghost big" id="back">Back</button></div>`;
    qs<HTMLButtonElement>(div, '#back').addEventListener('click', () => {
      location.hash = '#/pick';
    });
    return;
  }

  let busy = false;
  let stopCamera: (() => void) | null = null;
  let destroyed = false;
  let flash: { kind: 'good' | 'bad' | 'warn'; text: string } | null = null;
  let queue = view.queue;

  function draw(): void {
    if (destroyed) return;
    const now = queue[0];
    const pct = view.total > 0 ? Math.round(((view.picked + view.short) / view.total) * 100) : 0;
    const head = `<div class="topbar"><h1 style="font-size:1rem;">${escapeHtml(view.name)}</h1></div>
      <div class="pick-bar"><i style="width:${pct}%"></i></div>
      <p class="hint" style="margin:0.25rem 0 0.75rem;"><b>${view.picked}</b> of ${view.total} orders picked${view.short > 0 ? ` · <span class="error-text">${view.short} short</span>` : ''}${view.excluded > 0 ? ` · ${view.excluded} not in Mintsoft batch (skip)` : ''}</p>`;
    if (!now) {
      div.innerHTML = `${head}<div class="card pick-done"><h2>Batch finished</h2><p>${view.picked} orders picked and recorded.</p>${view.short > 0 ? `<p class="error-text">${view.short} order${view.short === 1 ? ' was' : 's were'} marked short - the office has been told.</p>` : ''}</div>
        <div class="footer-actions"><button class="ghost big" id="undo">Undo last</button><button class="primary big" id="back">Back to batches</button></div>`;
      qs<HTMLButtonElement>(div, '#back').addEventListener('click', () => {
        location.hash = '#/pick';
      });
      qs<HTMLButtonElement>(div, '#undo').addEventListener('click', () => void undo());
      return;
    }
    const upcoming = queue
      .slice(1, 5)
      .map((st) =>
        st.kind === 'LABEL'
          ? `<div class="pick-up"><span>Label</span><span>Scan label · order ${escapeHtml(st.orderNumber)}</span><span></span></div>`
          : `<div class="pick-up"><span>${escapeHtml(st.location)}</span><span>${escapeHtml(st.sku)}</span><span>${st.qty > 1 ? `×${st.qty}` : ''}</span></div>`,
      );
    const retry = now.retry ? '<p class="pick-flash warn" style="margin:0 0 0.6rem;">Skipped earlier - have another look for this one.</p>' : '';
    const card =
      now.kind === 'LABEL'
        ? `<div class="card pick-now">
        <div class="pick-loc" style="font-size:2.2rem;">SCAN LABEL</div>
        <div class="pick-sku">Order ${escapeHtml(now.orderNumber)}</div>
        <div class="pick-order">${escapeHtml(now.firstName)} · scan the label that goes on this parcel</div>
      </div>`
        : `<div class="card pick-now">
        <div class="pick-loc">${escapeHtml(now.location || 'NO LOCATION')}</div>
        <div class="pick-sku">${escapeHtml(now.sku)}</div>
        <div class="pick-qty ${now.qty > 1 ? 'multi' : ''}">${now.qty > 1 ? `Take ${now.qty}` : 'Take 1'}</div>
        <div class="pick-stock" id="pickStock">${stockHtml(now, stockKey(now))}</div>
        <div class="pick-order">Order ${escapeHtml(now.orderNumber)} · ${escapeHtml(now.firstName)}${now.items > 1 ? ` · item ${now.itemNo} of ${now.items}` : ''}</div>
      </div>`;
    div.innerHTML = `${head}
      ${retry}${card}
      ${flash ? `<p class="pick-flash ${flash.kind}">${escapeHtml(flash.text)}</p>` : ''}
      <div class="scan-input-wrap">
        <input type="text" id="pickInput" placeholder="${now.kind === 'LABEL' ? 'Scan the label' : 'Scan the item'}" autocomplete="off" autocapitalize="off" autocorrect="off" spellcheck="false" ${busy ? 'disabled' : ''} />
        <button type="button" class="ghost camera-btn" id="pickCamera" aria-label="Scan with camera">📷</button>
      </div>
      <div class="actions-row" style="gap:0.5rem; margin-top:0.5rem;">
        <button class="secondary" id="manual" ${busy ? 'disabled' : ''}>${now.kind === 'LABEL' ? "Label won't scan - confirm" : 'No barcode - confirm manually'}</button>
        ${now.kind === 'LINE' ? `<button class="secondary" id="cant" ${busy ? 'disabled' : ''}>Can't find it</button>` : ''}
        <button class="ghost" id="undo" ${busy ? 'disabled' : ''}>Undo last</button>
      </div>
      ${upcoming.length ? `<h3 style="margin:1rem 0 0.25rem;">Next</h3>${upcoming.join('')}` : ''}
      <div class="footer-actions"><button class="ghost big" id="back">Batches</button></div>`;
    const input = qs<HTMLInputElement>(div, '#pickInput');
    input.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter') return;
      const v = input.value.trim();
      input.value = '';
      if (v) void confirm(v, false);
    });
    qs<HTMLButtonElement>(div, '#pickCamera').addEventListener('click', () => openCamera());
    qs<HTMLButtonElement>(div, '#manual').addEventListener('click', () => askManual(now));
    div.querySelector<HTMLButtonElement>('#cant')?.addEventListener('click', () => askSkip(now));
    qs<HTMLButtonElement>(div, '#undo').addEventListener('click', () => void undo());
    qs<HTMLButtonElement>(div, '#back').addEventListener('click', () => {
      location.hash = '#/pick';
    });
    if (!busy) input.focus({ preventScroll: true });
    for (const st of queue.slice(0, 3)) if (st.kind === 'LINE') void ensureStock(st);
  }

  /** Fetches the stock of a step (if not already known / fresh) and, once it arrives, fills the box without redrawing the screen. */
  async function ensureStock(st: PickStep): Promise<void> {
    const key = stockKey(st);
    const hit = stockCache.get(key);
    if ((hit && Date.now() - hit.at < STOCK_TTL_MS) || stockInFlight.has(key)) return;
    stockInFlight.add(key);
    try {
      const r = await api.pickStock(st.sku, st.location);
      stockCache.set(key, { at: Date.now(), stock: r.stock, error: '' });
    } catch (err) {
      const msg = err instanceof ApiError ? (err.status === 404 ? 'server not updated yet' : err.message) : err instanceof ApiNetworkError ? 'no connection' : String(err);
      stockCache.set(key, { at: Date.now() - STOCK_TTL_MS + 5_000, stock: null, error: msg.slice(0, 80) }); // try again in 5 seconds
    } finally {
      stockInFlight.delete(key);
    }
    if (stockCache.get(key)?.error && !stockRetried.has(key) && !destroyed) {
      stockRetried.add(key);
      setTimeout(() => void ensureStock(st), 5_000);
    }
    const now = queue[0];
    const box = destroyed ? null : div.querySelector<HTMLElement>('#pickStock');
    if (box && now && now.kind === 'LINE' && stockKey(now) === key) box.innerHTML = stockHtml(now, key);
  }

  function apply(r: PickConfirmResult): void {
    const i = view.orders.findIndex((o) => o.orderId === r.order.orderId);
    if (i >= 0) view.orders[i] = r.order;
    view.picked = r.picked;
    view.total = r.total;
    view.short = r.short;
    queue = r.queue;
  }

  /** Runs one server action with the busy / flash handling shared by every button. */
  async function act(working: string, call: () => Promise<PickConfirmResult>, done: (r: PickConfirmResult) => { kind: 'good' | 'bad' | 'warn'; text: string }): Promise<void> {
    if (busy) return;
    busy = true;
    flash = { kind: 'warn', text: working };
    draw();
    try {
      const r = await call();
      apply(r);
      flash = done(r);
      signal(flash.kind === 'good' ? 'good' : 'warn');
    } catch (err) {
      flash = { kind: 'bad', text: describeError(err) };
      signal('bad');
    } finally {
      busy = false;
      draw();
    }
  }

  async function confirm(scanned: string, manual: boolean): Promise<void> {
    const now = queue[0];
    if (!now) return;
    if (now.kind === 'LABEL') {
      await act('Checking label…', () => api.pickLabel({ runId, batchId, orderId: now.orderId, scanned, manual }), (r) =>
        r.alreadyDone
          ? { kind: 'warn', text: 'That label was already confirmed.' }
          : { kind: r.unverified ? 'warn' : 'good', text: r.unverified ? `Order picked ✓ (label could not be checked - no tracking in Mintsoft yet)  (${r.picked} of ${r.total})` : `Label OK - order picked ✓  (${r.picked} of ${r.total})` },
      );
      return;
    }
    await act('Checking…', () => api.pickConfirm({ runId, batchId, orderId: now.orderId, lineIdx: now.lineIdx, scanned, manual }), (r) =>
      r.alreadyDone
        ? { kind: 'warn', text: 'That line was already confirmed.' }
        : { kind: 'good', text: r.orderCompleted ? `Order picked ✓  (${r.picked} of ${r.total})` : r.queue[0]?.kind === 'LABEL' ? 'Items done ✓ - now scan the label' : 'Item confirmed ✓' },
    );
  }

  async function skip(step: PickStep, reason: string, note: string, final: boolean): Promise<void> {
    await act('Saving…', () => api.pickSkip({ runId, batchId, orderId: step.orderId, lineIdx: step.lineIdx, reason, note, final }), () =>
      final ? { kind: 'warn', text: `Order ${step.orderNumber} marked short - the office will see it.` } : { kind: 'warn', text: `Order ${step.orderNumber} skipped - you will come back to it at the end.` },
    );
  }

  async function undo(): Promise<void> {
    await act('Undoing…', () => api.pickUndo({ runId, batchId }), () => ({ kind: 'warn', text: 'Last action taken back.' }));
  }

  function askManual(step: PickStep): void {
    const label = step.kind === 'LABEL';
    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop';
    backdrop.innerHTML = `<div class="modal"><h2>Confirm without scanning?</h2><p>${label ? 'Only use this when the <b>label will not scan</b>.' : 'Only use this when the item has <b>no barcode</b>.'} It is recorded against your name and the office sees it.</p><p class="hint">${escapeHtml(label ? `Order ${step.orderNumber}` : step.sku)}</p><div class="actions-row" style="gap:0.5rem;"><button class="ghost" id="no">Cancel</button><button class="secondary" id="yes">${label ? 'Yes, it will not scan' : 'Yes, it has no barcode'}</button></div></div>`;
    document.body.appendChild(backdrop);
    qs<HTMLButtonElement>(backdrop, '#no').addEventListener('click', () => backdrop.remove());
    qs<HTMLButtonElement>(backdrop, '#yes').addEventListener('click', () => {
      backdrop.remove();
      void confirm('', true);
    });
  }

  function askSkip(step: PickStep): void {
    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop';
    const reasons: Array<[string, string]> = [['NOT_AT_LOCATION', 'Not at the location'], ['DAMAGED', 'Damaged'], ['WRONG_LOCATION', 'Stock is somewhere else'], ['OTHER', 'Other']];
    backdrop.innerHTML = `<div class="modal"><h2>Can't find it?</h2>
      <p class="hint">${escapeHtml(step.sku)} · ${escapeHtml(step.location)} · order ${escapeHtml(step.orderNumber)}</p>
      <div class="pick-reasons">${reasons.map(([v, t], i) => `<label><input type="radio" name="why" value="${v}" ${i === 0 ? 'checked' : ''}> ${escapeHtml(t)}</label>`).join('')}</div>
      <input type="text" id="whyNote" placeholder="Note (needed for Other)" maxlength="150" style="width:100%;margin:0.5rem 0;" />
      <p class="error-text" id="whyErr"></p>
      <div class="actions-row" style="gap:0.5rem;flex-wrap:wrap;"><button class="ghost" id="no">Cancel</button><button class="secondary" id="later">Skip - come back at the end</button></div>
      <div class="actions-row" style="margin-top:0.5rem;"><button class="ghost" id="short" style="color:var(--bad);">Mark short - it cannot be picked</button></div>
      <p class="hint">Short means the order is not picked and not counted; the office is told.</p></div>`;
    document.body.appendChild(backdrop);
    const choose = (final: boolean): void => {
      const why = backdrop.querySelector<HTMLInputElement>('input[name="why"]:checked')?.value ?? 'OTHER';
      const note = qs<HTMLInputElement>(backdrop, '#whyNote').value.trim();
      if (why === 'OTHER' && note === '') {
        qs<HTMLParagraphElement>(backdrop, '#whyErr').textContent = 'Say what the problem is.';
        return;
      }
      backdrop.remove();
      void skip(step, why, note, final);
    };
    qs<HTMLButtonElement>(backdrop, '#no').addEventListener('click', () => backdrop.remove());
    qs<HTMLButtonElement>(backdrop, '#later').addEventListener('click', () => choose(false));
    qs<HTMLButtonElement>(backdrop, '#short').addEventListener('click', () => choose(true));
  }

  function openCamera(): void {
    if (!navigator.mediaDevices?.getUserMedia) {
      flash = { kind: 'bad', text: 'This browser cannot use the camera. Use the scanner or type the barcode.' };
      draw();
      return;
    }
    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop';
    backdrop.innerHTML = `<div class="modal camera-modal"><h2>Scan with Camera</h2><p class="hint" id="camStatus">Starting camera…</p><video id="camVideo" autoplay playsinline muted></video><button class="ghost" id="camCancel" style="margin-top:0.75rem;">Cancel</button></div>`;
    document.body.appendChild(backdrop);
    const video = qs<HTMLVideoElement>(backdrop, '#camVideo');
    const status = qs<HTMLParagraphElement>(backdrop, '#camStatus');
    let controls: { stop: () => void } | null = null;
    let stopped = false;
    const close = () => {
      if (!stopped) {
        stopped = true;
        controls?.stop();
        stopCamera = null;
      }
      backdrop.remove();
    };
    stopCamera = close;
    qs<HTMLButtonElement>(backdrop, '#camCancel').addEventListener('click', close);
    const reader = new BrowserMultiFormatReader(queue[0]?.kind === 'LABEL' ? LABEL_CAMERA_HINTS : CAMERA_HINTS);
    let handled = false;
    void (async () => {
      try {
        controls = await reader.decodeFromConstraints({ video: { facingMode: 'environment' } }, video, (result) => {
          if (stopped || handled || !result) return;
          handled = true;
          const value = result.getText();
          close();
          void confirm(value, false);
        });
        if (stopped) controls.stop();
        else status.textContent = 'Point the camera at the item barcode.';
      } catch {
        status.classList.add('error-text');
        status.textContent = "Couldn't access the camera. Check permissions and try again.";
      }
    })();
  }

  draw();
  const cleanup = () => {
    destroyed = true;
    stopCamera?.();
    window.removeEventListener('hashchange', cleanup);
  };
  window.addEventListener('hashchange', cleanup, { once: true });
}
